import React, { useRef, useState } from 'react';
import { Box, TextField, Typography, FormHelperText } from '@mui/material';

/**
 * Professional National ID Input Component
 * Displays 14 separate input boxes for each digit
 */
const NationalIdInput = ({ value = '', onChange, error, disabled = false }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(() => {
    const initialDigits = value.split('').slice(0, 14);
    return [...initialDigits, ...Array(14 - initialDigits.length).fill('')];
  });

  const handleDigitChange = (index, newValue) => {
    // Allow only numeric input
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    // Call parent onChange with complete value
    const completeValue = newDigits.join('');
    if (onChange) {
      onChange({ target: { name: 'ssn', value: completeValue } });
    }

    // Auto-focus next input
    if (newValue && index < 13) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 13) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 14);
    const newDigits = pastedData.split('');
    const updatedDigits = [...newDigits, ...Array(14 - newDigits.length).fill('')];
    setDigits(updatedDigits);

    if (onChange) {
      onChange({ target: { name: 'ssn', value: pastedData } });
    }

    // Focus the next empty input or last input
    const nextEmptyIndex = newDigits.length < 14 ? newDigits.length : 13;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: error ? 'error.main' : 'text.secondary',
          fontWeight: 500,
          fontSize: '0.875rem',
        }}
      >
        National ID (14 digits)
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: { xs: 0.5, sm: 1 },
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '12px 0',
              },
            }}
            sx={{
              width: { xs: '32px', sm: '45px' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: error ? 'error.main' : 'divider',
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: error ? 'error.main' : 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? 'error.main' : 'primary.main',
                  borderWidth: '2px',
                },
                '& input': {
                  color: error ? 'error.main' : 'text.primary',
                },
              },
            }}
          />
        ))}
      </Box>

      {error && (
        <FormHelperText error sx={{ mt: 1, ml: 0 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default NationalIdInput;
